/**
 * Multi-org helpers — list the user's orgs and switch the active one.
 *
 * Backs the OrgPicker in AppShell. A user can belong to many orgs via
 * OrgMember rows; the JWT carries exactly one org_id (the "active" org).
 * Switching rotates the rq_session cookie server-side and stamps
 * users.last_active_org_id so future logins return to the same org.
 *
 * Server endpoints (already shipped on retention-iq):
 *   GET  /api/me/orgs        — every (org, role) the user belongs to
 *   POST /api/me/active-org  — { org_id } → rotate cookie + persist pointer
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { MyOrg } from "@/lib/types";

export type { MyOrg };

export function useMyOrgs() {
  return useQuery({
    queryKey: ["me", "orgs"],
    queryFn: api.listMyOrgs,
    staleTime: 60_000,
  });
}

/**
 * Switch active org. The server rotates the JWT cookie; on success we wipe
 * the entire query cache (server state is now a different org) and hard-reload
 * to /portfolio so the user lands in the new org's brand list. A hard reload
 * (not just invalidation) is correct because an org swap changes "what data
 * exists", not just its values.
 */
export function useSetActiveOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => api.setActiveOrg(orgId),
    onSuccess: () => {
      qc.clear();
      if (typeof window !== "undefined") {
        window.location.href = "/dashboard";
      }
    },
  });
}
